import React from "react";
import { Icon } from "@iconify/react";

const DAYS = [
  "monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday"
];

const BusinessHoursSelector = ({ value, onChange }) => {
  const hours = value || {};

  // HELPER: Validate time range (Start must be before End)
  const isInvalidRange = (slot) => {
    if (!slot || slot === "Closed" || slot === "00:00-23:59") return false;
    const [start, end] = slot.split("-");
    return start >= end;
  };

  const handleToggleDay = (day) => {
    const isClosed = hours[day] === "Closed" || !hours[day];
    const newValue = isClosed ? "09:00-18:00" : "Closed";
    onChange({ ...hours, [day]: newValue });
  };

  const handleTimeChange = (day, index, part, timeValue) => {
    const dayHours = (hours[day] || "").split(", ");
    const slots = dayHours.map((slot, i) => {
      if (i !== index) return slot;
      const [start, end] = slot.split("-");
      return part === "start" ? `${timeValue}-${end}` : `${start}-${timeValue}`;
    });
    onChange({ ...hours, [day]: slots.join(", ") });
  };

  const set24Hours = (day) => {
    onChange({ ...hours, [day]: "00:00-23:59" });
  };

  const addSlot = (day) => {
    const current = hours[day];
    const newValue = current === "Closed" || current === "00:00-23:59" 
      ? "09:00-18:00" 
      : `${current}, 09:00-18:00`;
    onChange({ ...hours, [day]: newValue });
  };

  const removeSlot = (day, index) => {
    const slots = hours[day].split(", ").filter((_, i) => i !== index);
    const newValue = slots.length === 0 ? "Closed" : slots.join(", ");
    onChange({ ...hours, [day]: newValue });
  };

  const clearAll = () => {
    if (window.confirm("Are you sure you want to clear all hours?")) {
      const reset = {};
      DAYS.forEach(d => reset[d] = "Closed");
      onChange(reset);
    }
  };

  const copyMondayToAll = () => {
    const mondayValue = hours["monday"] || "Closed";
    const updatedHours = { ...hours };
    DAYS.forEach((day) => {
      if (day !== "monday" && hours[day] !== "Closed") {
        updatedHours[day] = mondayValue;
      }
    });
    onChange(updatedHours);
  };

  return (
    <div className="business-hours-container bg-white p-24 radius-12 border shadow-sm">
      <div className="d-flex justify-content-between align-items-center mb-24 border-bottom pb-16">
        <h6 className="mb-0 text-primary-light">Opening Hours</h6>
        <button type="button" className="btn btn-sm btn-outline-danger radius-8 px-16" onClick={clearAll}>
          <Icon icon="lucide:trash-2" className="me-1" /> Clear All
        </button>
      </div>

      {DAYS.map((day) => {
        const isOpen = hours[day] && hours[day] !== "Closed";
        const is24Hours = hours[day] === "00:00-23:59";
        const slots = isOpen ? hours[day].split(", ") : [];

        return (
          <div key={day} className="row mb-12 align-items-start pb-12 border-bottom last-border-0">
            <div className="col-md-3 py-8 d-flex align-items-center">
              <div className="form-check form-switch me-3">
                <input className="form-check-input" type="checkbox" checked={isOpen} onChange={() => handleToggleDay(day)} />
              </div>
              <label className="fw-bold text-capitalize mb-0" style={{ minWidth: '80px' }}>{day}</label>
              {day === "monday" && isOpen && (
                <button type="button" className="btn btn-sm btn-info-focus text-info-main ms-auto p-4 radius-4" onClick={copyMondayToAll} title="Copy Monday to others">
                  <Icon icon="lucide:copy" width="16" />
                </button>
              )}
            </div>

            <div className="col-md-9">
              {!isOpen ? (
                <span className="text-secondary-light py-8 d-inline-block italic small">Closed</span>
              ) : (
                <div className="d-flex flex-column gap-2">
                  {slots.map((slot, index) => {
                    const [start, end] = (slot || "09:00-18:00").split("-");
                    const invalid = isInvalidRange(slot);

                    return (
                      <div key={index} className="d-flex align-items-center gap-2">
                        <div className={`input-group input-group-sm w-auto shadow-sm radius-4 overflow-hidden border ${invalid ? 'border-danger' : 'border-neutral-200'}`}>
                          <input
                            type="time"
                            className={`form-control border-0 ${invalid ? 'bg-danger-50' : 'bg-neutral-50'}`}
                            disabled={is24Hours}
                            value={start}
                            onChange={(e) => handleTimeChange(day, index, "start", e.target.value)}
                          />
                          <span className={`input-group-text border-0 ${invalid ? 'bg-danger-50' : 'bg-neutral-50'}`}>to</span>
                          <input
                            type="time"
                            className={`form-control border-0 ${invalid ? 'bg-danger-50' : 'bg-neutral-50'}`}
                            disabled={is24Hours}
                            value={end}
                            onChange={(e) => handleTimeChange(day, index, "end", e.target.value)}
                          />
                        </div>
                        
                        <div className="d-flex align-items-center gap-1">
                          {invalid && <Icon icon="lucide:alert-circle" className="text-danger-main" width="20" title="End time must be after start time" />}
                          {index === 0 && (
                            <>
                              <button type="button" className={`btn btn-sm radius-4 px-8 py-4 ${is24Hours ? 'btn-primary' : 'btn-outline-neutral'}`} onClick={() => set24Hours(day)}>
                                <span className="small fw-bold">24h</span>
                              </button>
                              <button type="button" className="btn btn-sm text-primary p-4 shadow-none" onClick={() => addSlot(day)}><Icon icon="lucide:plus-circle" width="20" /></button>
                            </>
                          )}
                          {index > 0 && (
                            <button type="button" className="btn btn-sm text-danger p-4 shadow-none" onClick={() => removeSlot(day, index)}><Icon icon="lucide:minus-circle" width="20" /></button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default BusinessHoursSelector;